import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { api } from "../lib/api"
import type { FormConfig, Lead } from "../types/form-types"
import { getDeviceInfo } from "@/utils/device-info"
import { referralApi } from "@/lib/api-referral"

export default function useWidget(mode: string) {
	api.interceptors.request.use(config => {
		config.headers["mode"] = mode
		return config
	})
	referralApi.interceptors.request.use(config => {
		config.headers["mode"] = mode
		return config
	})

	const useGetWidgetById = (universityId: string, widgetId: string) => {
		return useSuspenseQuery({
			queryFn: async () => {
				const { data } = await api.get<FormConfig>(
					`universities/${universityId}/widgets/${widgetId}`,
				)
				return data
			},
			queryKey: ["widget", universityId, widgetId],
		})
	}

	const useValidateLead = (universityId: string, widgetId: string) => {
		return useMutation({
			mutationFn: async ({
				email = "",
				countryCode = "",
				phoneNumber = "",
			}: {
				email?: string
				countryCode?: string
				phoneNumber?: string
			}) => {
				const { data } = await api.get<Lead | null>(
					`universities/${universityId}/widgets/${widgetId}/validate-lead?email=${email}&country_code=${countryCode}&phone=${phoneNumber}`,
				)
				return data
			},
			mutationKey: ["validate-lead"],
		})
	}

	const useSubmitWidgetStage = (
		universityId: string,
		widgetId: string,
		stageId: string,
		associatedFormId: string,
		applicationId: string,
		params: any,
		metaData: any,
	) => {
		return useMutation({
			mutationFn: async ({
				payload,
				isCreateApplication,
				leadId,
			}: {
				payload: Record<string, unknown>
				isCreateApplication: boolean
				leadId: string
			}) => {
				const formData = new FormData()
				if (params)
					formData.append("lead_utm_params", JSON.stringify(params))

				const deviceInfo = getDeviceInfo(window)
				let modifiedMetaData = { ...deviceInfo }
				if (metaData) {
					modifiedMetaData = { ...modifiedMetaData, ...metaData }
				}
				formData.append(
					"lead_meta_data",
					JSON.stringify(modifiedMetaData),
				)

				for (const [key, value] of Object.entries(payload)) {
					if (value !== null && value !== undefined) {
						if (Array.isArray(value) && value[0] instanceof File) {
							// Handle multiple files
							for (const file of value as File[]) {
								formData.append(key, file)
							}
						} else if (value instanceof File) {
							// Handle single file
							formData.append(key, value)
						} else if (value instanceof Date) {
							// Handle Date object
							formData.append(key, value.toISOString())
						} else if (typeof value === "object") {
							formData.append(key, JSON.stringify(value))
						} else {
							// Handle strings, numbers, booleans
							formData.append(key, `${value}`)
						}
					}
				}

				const partnerId = window.__LP_PARTNERID__ || ""
				const counsellorId = window.__LP_COUNSELLERID__ || ""

				const { data } = await api.post<{
					application_id: string
					lead_id: string
					domain: string
				}>(
					`universities/${universityId}/widgets/${widgetId}/stages/${stageId}/submit?create_application=${isCreateApplication}&lead_id=${leadId}&associated_form_id=${associatedFormId}&application_id=${applicationId}${partnerId ? `&partner_id=${partnerId}` : ""}${counsellorId ? `&counsellor_id=${counsellorId}` : ""}`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				)

				return data
			},
			mutationKey: [
				"stage-submission",
				universityId,
				widgetId,
				stageId,
				applicationId,
			],
		})
	}

	const useReferralWidgetSubmition = (
		universityId: string,
		widgetId: string,
		associatedFormId: string,
		params: {
			campaign_id: string
		} | null,
		metaData: any,
		source: string,
	) => {
		return useMutation({
			mutationFn: async ({
				payload,
			}: {
				payload: Record<string, unknown>
			}) => {
				const formData = new FormData()
				if (params)
					formData.append(
						"referral_utm_params",
						JSON.stringify(params),
					)

				const deviceInfo = getDeviceInfo(window)
				let modifiedMetaData = { ...deviceInfo }
				if (metaData) {
					modifiedMetaData = { ...modifiedMetaData, ...metaData }
				}
				formData.append(
					"referral_meta_data",
					JSON.stringify(modifiedMetaData),
				)

				for (const [key, value] of Object.entries(payload)) {
					if (value !== null && value !== undefined) {
						if (value instanceof Date) {
							// Handle Date object
							formData.append(key, value.toISOString())
						} else if (typeof value === "object") {
							formData.append(key, JSON.stringify(value))
						} else {
							// Handle strings, numbers, booleans
							formData.append(key, `${value}`)
						}
					}
				}

				const campaignId = params?.campaign_id || ""

				const { data } = await referralApi.post<{
					application_id: string
					lead_id: string
					domain: string
				}>(
					`universities/${universityId}/widgets/referral-invitation?widget_id=${widgetId}&associated_form_id=${associatedFormId}&source=${source}&campaign_id=${campaignId}`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				)

				return data
			},
			mutationKey: ["stage-submission", universityId, widgetId],
		})
	}

	return {
		useGetWidgetById,
		useValidateLead,
		useSubmitWidgetStage,
		useReferralWidgetSubmition,
	}
}
